/**
 * «Сегодня» — экран родителя.
 *
 * Смысл экрана: открыть, увидеть, что с ребёнком всё в порядке, и закрыть.
 * Поэтому сверху статус смены одной строкой, дальше лента, а действий
 * минимум — родитель здесь читатель, а не оператор.
 */

import { useState } from 'react'

import { useCurrency, useFamily, useSession, useTimeZone } from '@/app/session'
import { MapSheet } from '@/features/geo/MapSheet'
import { PhotoViewer } from '@/features/media/PhotoViewer'
import { ParentShiftCard } from '@/features/shift/ShiftCard'
import { DaySummaryCard } from '@/features/trust/TrustPanel'
import { Timeline } from '@/features/timeline/Timeline'
import {
  useApproveExpense,
  useCreateEvent,
  useDay,
  useRejectExpense,
  type ExpenseCard,
  type GeoPoint,
} from '@/shared/api/day'
import { useDaySummary } from '@/shared/api/trust'
import { formatLongDay, formatMoney } from '@/shared/format'
import { Button } from '@/shared/ui/Button'
import { Screen } from '@/shared/ui/Screen'
import { Sheet } from '@/shared/ui/Sheet'
import { Field, TextInput } from '@/shared/ui/fields'
import { SectionTitle } from '@/shared/ui/primitives'
import { ErrorState, SkeletonCard } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

export function Today() {
  const me = useSession((state) => state.me)
  const family = useFamily()
  const timeZone = useTimeZone()
  const currency = useCurrency()
  const familyId = family?.id ?? 0

  const [mapGeo, setMapGeo] = useState<GeoPoint | null>(null)
  const [photos, setPhotos] = useState<{ urls: string[]; index: number } | null>(null)
  const [noteOpen, setNoteOpen] = useState(false)
  const [note, setNote] = useState('')
  const [pendingExpense, setPendingExpense] = useState<ExpenseCard | null>(null)

  const day = useDay(familyId, undefined, true)
  const summary = useDaySummary(familyId)

  const addNote = useCreateEvent(familyId, {
    onSuccess: () => {
      setNote('')
      setNoteOpen(false)
      toast.success('Заметка добавлена')
    },
    onError: (error) => toast.error(error.message),
  })

  if (!me || !family) return null
  if (day.isLoading) {
    return (
      <Screen title="Сегодня">
        <SkeletonCard lines={2} />
        <SkeletonCard lines={4} />
      </Screen>
    )
  }
  if (day.error) {
    return (
      <Screen title="Сегодня">
        <ErrorState error={day.error} onRetry={() => void day.refetch()} />
      </Screen>
    )
  }

  const data = day.data!
  const nanny = family.members.find((member) => member.role === 'nanny')
  const nannyName = nanny?.first_name ?? 'Няня'
  const latestShift = data.active_shift ?? data.shifts[data.shifts.length - 1]

  return (
    <Screen
      title="Сегодня"
      subtitle={formatLongDay(new Date(), timeZone)}
      right={
        <Button size="sm" variant="secondary" onClick={() => setNoteOpen(true)}>
          Заметка
        </Button>
      }
    >
      <ParentShiftCard
        shift={latestShift}
        minutesSoFar={data.minutes_so_far}
        currency={currency}
        timeZone={timeZone}
        nannyName={nannyName}
        onGeoPress={setMapGeo}
      />

      {summary.data && <DaySummaryCard summary={summary.data} currency={currency} />}

      <div data-coach="parent_day">
        <SectionTitle>Лента дня</SectionTitle>
        <Timeline
          events={data.events}
          expenses={data.expenses}
          timeZone={timeZone}
          currency={currency}
          emptyTitle="Пока тихо"
          emptyDescription={`Как только ${nannyName} что-то отметит, это появится здесь.`}
          onPhotoPress={(urls, index) => setPhotos({ urls, index })}
          onGeoPress={setMapGeo}
          onExpensePress={(expense) =>
            expense.status === 'pending' ? setPendingExpense(expense) : undefined
          }
        />
      </div>

      <Sheet
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        title="Заметка в ленту"
        footer={
          <Button
            block
            size="lg"
            loading={addNote.isPending}
            disabled={!note.trim()}
            onClick={() => addNote.mutate({ type_code: 'note', note: note.trim() })}
          >
            Добавить
          </Button>
        }
      >
        <Field label="Что записать" hint="Няня увидит заметку в своей ленте дня.">
          <TextInput
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Сегодня заберу сама в 17:00"
            maxLength={500}
            autoFocus
          />
        </Field>
      </Sheet>

      <ExpenseDecisionSheet
        expense={pendingExpense}
        familyId={familyId}
        currency={currency}
        onClose={() => setPendingExpense(null)}
      />

      <MapSheet geo={mapGeo} timeZone={timeZone} onClose={() => setMapGeo(null)} />
      <PhotoViewer
        urls={photos?.urls ?? null}
        startIndex={photos?.index ?? 0}
        onClose={() => setPhotos(null)}
      />
    </Screen>
  )
}

/** Решение по расходу прямо из ленты: «Ок» в один тап, отказ — с причиной. */
function ExpenseDecisionSheet({
  expense,
  familyId,
  currency,
  onClose,
}: {
  expense: ExpenseCard | null
  familyId: number
  currency: string
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const [rejecting, setRejecting] = useState(false)

  const approve = useApproveExpense(familyId, {
    onSuccess: () => {
      toast.success('Расход одобрен')
      onClose()
    },
    onError: (error) => toast.error(error.message),
  })
  const reject = useRejectExpense(familyId, {
    onSuccess: () => {
      toast.show('Расход отклонён')
      setReason('')
      setRejecting(false)
      onClose()
    },
    onError: (error) => toast.error(error.message),
  })

  return (
    <Sheet
      open={expense !== null}
      onClose={() => {
        setRejecting(false)
        setReason('')
        onClose()
      }}
      title={expense ? `${expense.title} · ${formatMoney(expense.amount, currency)}` : ''}
    >
      {expense && (
        <>
          {expense.note && (
            <p className="text-text-muted text-body leading-snug">{expense.note}</p>
          )}
          {expense.receipt_urls.length > 0 && (
            <div className="mt-3 flex gap-2">
              {expense.receipt_urls.map((url) => (
                <img key={url} src={url} alt="" className="size-24 rounded-[var(--radius-thumb)] object-cover" />
              ))}
            </div>
          )}

          {rejecting ? (
            <div className="mt-4">
              <Field
                label="Почему отклоняете"
                hint="Няня увидит причину — напишите так, как сказали бы вслух."
              >
                <TextInput
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Это не по работе"
                  maxLength={200}
                  autoFocus
                />
              </Field>
              <Button
                className="mt-3"
                block
                size="lg"
                variant="danger"
                loading={reject.isPending}
                disabled={!reason.trim()}
                onClick={() => reject.mutate({ id: expense.id, reason: reason.trim() })}
              >
                Отклонить
              </Button>
              <Button
                block
                variant="ghost"
                onClick={() => setRejecting(false)}
                hapticKind="none"
              >
                Назад
              </Button>
            </div>
          ) : (
            <div className="mt-5 space-y-2">
              <Button
                block
                size="lg"
                loading={approve.isPending}
                hapticKind="success"
                onClick={() => approve.mutate(expense.id)}
              >
                Ок, компенсируем
              </Button>
              <Button block size="lg" variant="ghost" onClick={() => setRejecting(true)}>
                Отклонить
              </Button>
            </div>
          )}
        </>
      )}
    </Sheet>
  )
}
